import React from 'react'
import { useIntl } from 'react-intl'
import { useRecoilValue, useRecoilCallback } from 'recoil'
import PropTypes from 'prop-types'

import {
  Actions,
  Checkbox,
  TruncateAndTooltip,
  Grid,
  GridCell,
  Flex,
  FlexItem,
  Text,
  TEXT_VARIANT_BOLD,
  TEXT_VARIANT_LIGHT,
  SkeletonLoader
} from 'mailup-web-components'

import { singleCheckedSelector } from '../../recoil/state/results'

import { STORE_KEY_LIST } from 'Constants'
import { fromUTCtoZoneFormatted } from 'Helpers/dates'
import { storeGet } from 'Helpers/localStorage'
import { pageState } from 'Pages/SMTPPlusList/recoil/state/page'

export const Result = ({
  onCheck,
  message,
  userHasStats,
  onDeleteAction,
  onStatsAction
}) => {
  const { Id, Subject, Note, LastUpdated, LastSent } = message
  const intl = useIntl()

  const checked = useRecoilValue(singleCheckedSelector(Id))

  const tzcode = storeGet(STORE_KEY_LIST).TimeZoneCode

  const actionItems = [
    userHasStats && LastSent
      ? {
          label: intl.formatMessage({
            id: 'Load.MailupCommon.ActionStatistics'
          }),
          onClick: () => onStatsAction(Id),
          'data-mu-test': `action__statistics-${Id}`,
          className: `test-action__statistics-${Id}`
        }
      : undefined,
    {
      separator: !!(userHasStats && LastSent),
      label: intl.formatMessage({ id: 'Load.MailupCommon.Delete' }),
      onClick: () => onDeleteAction([Id]),
      'data-mu-test': `action__delete-${Id}`,
      className: `test-action__delete-${Id}`
    }
  ].filter(action => !!action)

  const handleCheck = useRecoilCallback(({ snapshot, set }) => async e => {
    const prevChecked = await snapshot.getPromise(singleCheckedSelector(Id))
    set(singleCheckedSelector(Id), !prevChecked)
  })

  const { fetchingResults } = useRecoilValue(pageState)
  if (fetchingResults) {
    return (
      <Flex columnGap='24px' width='100%'>
        <Checkbox
          disabled={fetchingResults}
          checked={checked}
          value={Id}
          onChange={handleCheck}
          data-mu-test={`item-check-${Id}`}
        />

        <FlexItem width='100%'>
          <Grid cols={12}>
            <GridCell span={6}>
              <SkeletonLoader />
              <SkeletonLoader />
            </GridCell>
            <GridCell span={3}>
              <Flex
                justifyContent='center'
                alignItems='flex-end'
                flexDirection='column'
                padding='0 24px 0 0'
              >
                <SkeletonLoader />
                <SkeletonLoader />
              </Flex>
            </GridCell>
            <GridCell span={3}>
              <Flex
                justifyContent='center'
                alignItems='flex-end'
                flexDirection='column'
                padding='0 24px 0 0'
              >
                <SkeletonLoader />
                <SkeletonLoader />
              </Flex>
            </GridCell>
          </Grid>
        </FlexItem>
        <FlexItem margin='0 0 0 8px'>
          <Actions items={actionItems} className='test-actions' />
        </FlexItem>
      </Flex>
    )
  }

  return (
    <Flex columnGap='24px' width='100%'>
      <Checkbox
        checked={checked}
        value={Id}
        onChange={handleCheck}
        data-mu-test={`item-check-${Id}`}
      />

      <FlexItem width='100%'>
        <Grid cols={12}>
          <GridCell span={6}>
            <Text variant={TEXT_VARIANT_BOLD}>
              <TruncateAndTooltip
                text={Subject}
                data-mu-test={`item-subject-${Id}`}
              />
            </Text>
            <Text
              variant={TEXT_VARIANT_LIGHT}
              data-mu-test={`item-notes-${Id}`}
            >
              {`${Id} ${Note && `- ${Note}`}`}
            </Text>
          </GridCell>
          <GridCell span={3}>
            <Flex
              justifyContent='center'
              alignItems='flex-end'
              flexDirection='column'
              padding='0 24px 0 0'
            >
              <Text
                variant={TEXT_VARIANT_LIGHT}
                data-mu-test={`item-last-updated-date-${Id}`}
              >
                {LastUpdated &&
                  fromUTCtoZoneFormatted(LastUpdated, tzcode, 'YYYY-MM-DD')}
              </Text>
              <Text
                variant={TEXT_VARIANT_LIGHT}
                data-mu-test={`item-last-updated-time-${Id}`}
              >
                {LastUpdated &&
                  fromUTCtoZoneFormatted(LastUpdated, tzcode, 'HH:mm:ss')}
              </Text>
            </Flex>
          </GridCell>
          <GridCell span={3}>
            <Flex
              justifyContent='center'
              alignItems='flex-end'
              flexDirection='column'
              padding='0 24px 0 0'
            >
              <Text
                variant={TEXT_VARIANT_LIGHT}
                data-mu-test={`item-last-sent-date-${Id}`}
              >
                {LastSent &&
                  fromUTCtoZoneFormatted(LastSent, tzcode, 'YYYY-MM-DD')}
              </Text>
              <Text
                variant={TEXT_VARIANT_LIGHT}
                data-mu-test={`item-last-sent-time-${Id}`}
              >
                {LastSent &&
                  fromUTCtoZoneFormatted(LastSent, tzcode, 'HH:mm:ss')}
              </Text>
            </Flex>
          </GridCell>
        </Grid>
      </FlexItem>
      <FlexItem margin='0 0 0 8px'>
        <Actions items={actionItems} className='test-actions' />
      </FlexItem>
    </Flex>
  )
}

Result.propTypes = {
  onCheck: PropTypes.func,
  message: PropTypes.object,
  userHasStats: PropTypes.bool,
  onDeleteAction: PropTypes.func,
  onStatsAction: PropTypes.funct
}
